import {
  fetchBudgetData,
  saveResourceBudget,
  saveRevision,
  type SaveRoleBudgetData,
} from '@/lib/data-service';
import type { RoleBudget } from '@/types/budget.types';

/**
 * Effective (post-save) Rate Card rates for a role, used to re-rate the
 * Resource Budget role rows before cascading into the Cost / Sales budgets.
 */
export interface EffectiveRoleRate {
  roleId?: number;
  roleName: string;
  hourlyCostRate: number;
  hourlyBillRate: number;
}

export interface RateChangeRoleImpact {
  roleName: string;
  budgetHours: number;
  previousCostRate: number;
  newCostRate: number;
  previousBillRate: number;
  newBillRate: number;
}

export interface RateChangeBudgetImpact {
  /** True when at least one budgeted role has a changed cost or bill rate. */
  hasChanges: boolean;
  previousCostLabour: number;
  newCostLabour: number;
  previousSalesLabour: number;
  newSalesLabour: number;
  costLabourDelta: number;
  salesLabourDelta: number;
  affectedRoles: RateChangeRoleImpact[];
  /** Resource Budget role rows re-rated with the new Rate Card rates. */
  roleBudgets: SaveRoleBudgetData[];
}

export interface RateChangeCascadeDecision {
  updateCostLabour: boolean;
  updateSalesLabour: boolean;
  overriddenByUser: boolean;
  cascadeTargetCategoryId?: number;
  cascadeTargetCategoryName?: string;
}

/** Rate Card role ids are prefixed (`role-12`); budget rows store the numeric id. */
function normaliseRoleId(roleId?: number | string): number | undefined {
  if (roleId === undefined || roleId === null) return undefined;
  const parsed = typeof roleId === 'number' ? roleId : parseInt(String(roleId).replace('role-', ''), 10);
  return Number.isNaN(parsed) ? undefined : parsed;
}

function findRate(role: RoleBudget, rates: EffectiveRoleRate[]): EffectiveRoleRate | undefined {
  const roleId = normaliseRoleId(role.roleId);
  if (roleId !== undefined) {
    const byId = rates.find(r => normaliseRoleId(r.roleId) === roleId);
    if (byId) return byId;
  }
  const name = (role.role || '').toLowerCase();
  return rates.find(r => r.roleName.toLowerCase() === name);
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Work out how the Cost and Sales Labour budgets would change if the Resource
 * Budget roles were re-rated with the supplied Rate Card rates.
 */
export function calculateRateChangeBudgetImpact(
  roleBudgets: RoleBudget[],
  rates: EffectiveRoleRate[]
): RateChangeBudgetImpact {
  const affectedRoles: RateChangeRoleImpact[] = [];
  const rerated: SaveRoleBudgetData[] = [];

  let previousCostLabour = 0;
  let newCostLabour = 0;
  let previousSalesLabour = 0;
  let newSalesLabour = 0;

  roleBudgets.forEach(role => {
    const hours = role.budgetHours || 0;
    const previousBillRate = role.billRate || 0;
    const previousCostRate = role.costRate ?? previousBillRate;
    const rate = findRate(role, rates);
    const newCostRate = rate ? rate.hourlyCostRate : previousCostRate;
    const newBillRate = rate ? rate.hourlyBillRate : previousBillRate;

    previousCostLabour += hours * previousCostRate;
    previousSalesLabour += hours * previousBillRate;
    newCostLabour += hours * newCostRate;
    newSalesLabour += hours * newBillRate;

    if (newCostRate !== previousCostRate || newBillRate !== previousBillRate) {
      affectedRoles.push({
        roleName: role.role,
        budgetHours: hours,
        previousCostRate,
        newCostRate,
        previousBillRate,
        newBillRate,
      });
    }

    rerated.push({
      id: role.id,
      roleId: role.roleId,
      role: role.role,
      budgetHours: hours,
      actualHours: role.actualHours,
      remainingHours: role.remainingHours,
      percentUsed: role.percentUsed,
      billRate: newBillRate,
      costRate: newCostRate,
      budgetCost: round2(hours * newCostRate),
      actualCost: role.actualCost,
      monthlyHours: role.monthlyHours,
      costCentreCode: role.costCentreCode ?? null,
    });
  });

  return {
    hasChanges: affectedRoles.length > 0,
    previousCostLabour: round2(previousCostLabour),
    newCostLabour: round2(newCostLabour),
    previousSalesLabour: round2(previousSalesLabour),
    newSalesLabour: round2(newSalesLabour),
    costLabourDelta: round2(newCostLabour - previousCostLabour),
    salesLabourDelta: round2(newSalesLabour - previousSalesLabour),
    affectedRoles,
    roleBudgets: rerated,
  };
}

/** Load the current Resource Budget roles and calculate the rate change impact. */
export async function fetchRateChangeBudgetImpact(
  projectId: number | string,
  rates: EffectiveRoleRate[]
): Promise<RateChangeBudgetImpact | null> {
  const budgetData = await fetchBudgetData(Number(projectId));
  const roles = budgetData?.resourceBudget?.byRole ?? [];
  if (roles.length === 0) return null;
  return calculateRateChangeBudgetImpact(roles, rates);
}

/**
 * Persist the re-rated Resource Budget roles and cascade the new Labour totals
 * into the Cost / Sales budgets via the existing budget revision API.
 */
export async function applyRateChangeBudgetCascade(
  projectId: number | string,
  impact: RateChangeBudgetImpact,
  decision: RateChangeCascadeDecision,
  notes?: string
): Promise<void> {
  const numericProjectId = Number(projectId);

  await saveResourceBudget({
    projectId: numericProjectId,
    byRole: impact.roleBudgets,
  });

  await saveRevision({
    projectId: numericProjectId,
    budgetLayer: 'resource',
    budgetTarget: 'category',
    status: 'active',
    reasonCategory: 'rate-change',
    reasonCategoryLabel: 'Rate Change',
    reasonDetail: notes?.trim() ? notes.trim() : 'Rate Card override rates updated',
    roleBudgets: impact.roleBudgets.map(role => ({
      roleId: role.roleId,
      roleName: role.role ?? role.roleName ?? '',
      budgetHours: role.budgetHours,
      billRate: role.billRate,
      costRate: role.costRate,
      budgetCost: role.budgetCost ?? 0,
      monthlyHours: role.monthlyHours ? JSON.stringify(role.monthlyHours) : undefined,
      costCentreCode: role.costCentreCode ?? undefined,
    })),
    cascadeDecision: decision,
  });
}
